import { useTranslation } from "react-i18next";
import { Button, Divider, Icon, Modal } from "semantic-ui-react";

import { Can } from "components";
import { useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import "modules/CheckInServices/CheckInServices.scss";
import { LocationServiceForm } from "modules/CheckInServices/components/LocationServiceModal/components";
import {
  useDisableCheckInServiceMutation,
  useUpdateNotificationEmailMutation,
  useUpdateSendToLefMutation
} from "modules/CheckInServices/components/LocationServiceModal/hooks";

import type { BrandGroupID, CheckInService, LEF_LEAD_KINDS } from "models";

export type LocationServiceFormValues = Omit<
  CheckInService,
  "id" | "dealer_id" | "created_on" | "updated_on" | "brand_groups" | "notification_email" | "send_to_lef" | "lef_lead_kind"
> & {
  brand_group_ids: BrandGroupID[];
  notification_email: string;
  send_to_lef: boolean;
  lef_lead_kind: LEF_LEAD_KINDS | null;
};

type LocationServiceModalProps = {
  isOpen: boolean;
  checkInService: CheckInService;
  onClose: () => void;
};

const FormHelper = createFormHelper<LocationServiceFormValues>();

export const LocationServiceModal = ({ isOpen, onClose, checkInService }: LocationServiceModalProps) => {
  const { t } = useTranslation();

  const { selectedLocation } = useSelectedLocation();

  const { mutate: disableService, isPending: isDisablePending } = useDisableCheckInServiceMutation();
  const { mutateAsync: updateNotificationEmail } = useUpdateNotificationEmailMutation();
  const { mutateAsync: updateSendToLef } = useUpdateSendToLefMutation();

  const handleSubmit = async (values: LocationServiceFormValues) => {
    await Promise.allSettled([
      updateSendToLef({
        send_to_lef: values.send_to_lef,
        lef_lead_kind: values.lef_lead_kind,
        checkin_service_id: checkInService.id,
        dealer_location_id: selectedLocation?.id ?? -1
      }),
      updateNotificationEmail({
        notification_email: values.notification_email,
        checkin_service_id: checkInService.id,
        dealer_location_id: selectedLocation?.id ?? -1
      })
    ]);

    onClose();
  };

  const handleDisable = () => {
    disableService(checkInService.id);
    onClose();
  };

  return (
    <Modal size="small" className="CheckInServiceModal" open={isOpen} onClose={onClose} closeOnDimmerClick={false}>
      <Modal.Header>
        <h3>{t("v8_edit_check_in_service", "Edit online check-in service")}</h3>
      </Modal.Header>

      <FormHelper.Form
        onSubmit={handleSubmit}
        defaultValues={{
          name: checkInService.name,
          description: checkInService.description,
          info_url: checkInService.info_url,
          price: checkInService.price,
          tag_id: checkInService.tag_id,
          can_convert_to_intervention: checkInService.can_convert_to_intervention,
          hide_for_lease_appointments: checkInService.hide_for_lease_appointments,
          brand_group_ids: checkInService.brand_groups?.map(brandGroup => brandGroup.id) ?? [],
          notification_email: checkInService.notification_email ?? "",
          send_to_lef: checkInService.send_to_lef ?? false,
          lef_lead_kind: checkInService.lef_lead_kind ?? null
        }}
      >
        <Modal.Content>
          <LocationServiceForm />
        </Modal.Content>

        <Divider fitted />

        <Modal.Actions>
          <div className="actions-left">
            <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.CheckInService}>
              <Button type="button" color="red" onClick={handleDisable} loading={isDisablePending}>
                {t("v8_disable", "Disable")}
              </Button>
            </Can>
          </div>

          <div className="actions-right">
            <Button type="button" className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Button>

            <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.CheckInService}>
              <Button type="submit" color="green">
                {t("v8_save", "Save")}
                <Icon className="check" />
              </Button>
            </Can>
          </div>
        </Modal.Actions>
      </FormHelper.Form>
    </Modal>
  );
};
